// @spec GHG-STR-023
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FileUp, Plane } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useGHGOrganizationId } from '@/features/ghg/api/use-ghg-organization-id';
import { BusinessTravelSummary } from '@/features/ghg/components/BusinessTravelSummary';
import { TravelEmissionsBreakdown } from '@/features/ghg/components/TravelEmissionsBreakdown';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/business-travel')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.businessTravel'),
  }),
  component: BusinessTravelPage,
});

/**
 * Scope 3 Category 6 (Business Travel) emissions page.
 *
 * Provides:
 * - Summary of total business travel emissions
 * - Breakdown by travel type (flights, hotels, rail, cars)
 * - Progress bars showing contribution of each type
 * - Pie chart visualization
 * - Data quality indicators
 * - Radiative forcing indicator for flights
 */
function BusinessTravelPage() {
  const { t } = useTranslation('ghg');
  const { isLoaded: isOrgLoaded } = useOrganization();
  const orgId = useGHGOrganizationId();
  const { periodId } = useGHGPagePeriod();

  if (!isOrgLoaded || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-5 w-80" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Page Header */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('businessTravelPage.heading')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('businessTravelPage.description')}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Button asChild>
              <Link to="/ghg/travel-import">
                <FileUp className="mr-2 h-4 w-4" />
                {t('businessTravelPage.importTravelData')}
              </Link>
            </Button>
          </div>
        </header>

        <div className="space-y-6">
            {/* Summary and Breakdown */}
            <div className="grid gap-6 lg:grid-cols-2">
              <BusinessTravelSummary reportingPeriodId={periodId} />
              <TravelEmissionsBreakdown reportingPeriodId={periodId} />
            </div>

            {/* Travel Types Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plane className="h-5 w-5" />
                  {t('businessTravelPage.aboutHeading')}
                </CardTitle>
                <CardDescription>
                  {t('businessTravelPage.aboutDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">{t('businessTravelPage.flights')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('businessTravelPage.flightsDesc')}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">{t('businessTravelPage.hotels')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('businessTravelPage.hotelsDesc')}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">{t('businessTravelPage.rail')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('businessTravelPage.railDesc')}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">{t('businessTravelPage.rentalCars')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('businessTravelPage.rentalCarsDesc')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
        </div>
      </div>
    </div>
  );
}
